import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdateEducation() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    educationStatus: '',
    educationLevel: '',
    otherEducationLevel: '',
    studyingLocation: '',
    schoolType: '',
    otherSchoolType: '',
    dropoutReason: '',
    otherDropoutReason: '',
    literacyLevel: [],
    usesEducationDevice: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message:
          'Missing required parameters. Please search for the record again.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getEducationDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load education details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      // Parse literacy level (comma-separated string to array)
      const literacyArray = response.record.literacyLevel
        ? response.record.literacyLevel
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s)
        : [];

      const ro = (val, options) =>
        val && !options.includes(val) ? 'Other' : val;
      const rot = (val, options) =>
        val && !options.includes(val) ? val : '';

      const lg = response.legend || {};
      const educationLevelOpts = lg.educationLevel || [];
      const schoolTypeOpts = lg.schoolType || [];
      const dropoutReasonOpts = lg.dropoutReason || [];

      const rawEducationLevel = response.record.educationLevel || '';
      const rawSchoolType = response.record.schoolType || '';
      const rawDropoutReason = response.record.dropoutReason || '';

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        educationStatus: response.record.educationStatus || '',
        educationLevel: ro(rawEducationLevel, educationLevelOpts),
        otherEducationLevel: rot(rawEducationLevel, educationLevelOpts),
        studyingLocation: response.record.studyingLocation || '',
        schoolType: ro(rawSchoolType, schoolTypeOpts),
        otherSchoolType: rot(rawSchoolType, schoolTypeOpts),
        dropoutReason: ro(rawDropoutReason, dropoutReasonOpts),
        otherDropoutReason: rot(rawDropoutReason, dropoutReasonOpts),
        literacyLevel: literacyArray,
        usesEducationDevice: response.record.usesEducationDevice || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updates = { [name]: value };

    // Handle education status change
    if (name === 'educationStatus') {
      if (value !== 'Currently studying') {
        updates.studyingLocation = '';
        updates.usesEducationDevice = '';
      }
      if (value !== 'Dropout student') {
        updates.dropoutReason = '';
        updates.otherDropoutReason = '';
      }
      if (value !== 'Currently studying' && value !== 'Dropout student') {
        updates.schoolType = '';
        updates.otherSchoolType = '';
      }
    }

    // Handle studying location change
    if (name === 'studyingLocation') {
      if (value !== 'School') {
        updates.schoolType = '';
        updates.otherSchoolType = '';
      }
    }

    const clearMap = {
      educationLevel: 'otherEducationLevel',
      schoolType: 'otherSchoolType',
      dropoutReason: 'otherDropoutReason',
    };
    if (value !== 'Other' && clearMap[name]) updates[clearMap[name]] = '';

    setFormData({
      ...formData,
      ...updates,
    });
  };

  const handleLiteracyChange = (e) => {
    const value = e.target.value;
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      literacyLevel: checked
        ? [...prev.literacyLevel, value]
        : prev.literacyLevel.filter((item) => item !== value),
    }));
  };

  const shouldShowStudyingLocation = () => {
    return formData.educationStatus === 'Currently studying';
  };

  const shouldShowSchoolType = () => {
    return (
      (formData.educationStatus === 'Currently studying' &&
        formData.studyingLocation === 'School') ||
      formData.educationStatus === 'Dropout student'
    );
  };

  const shouldShowDropoutReason = () => {
    return formData.educationStatus === 'Dropout student';
  };

  const shouldShowEducationDevices = () => {
    return formData.educationStatus === 'Currently studying';
  };

  const hasChanges = () => {
    // Compare all fields including array
    const current = {
      ...formData,
      literacyLevel: formData.literacyLevel.sort().join(', '),
    };
    const original = {
      ...originalData,
      literacyLevel: originalData.literacyLevel.sort().join(', '),
    };

    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.educationStatus || !formData.educationLevel) {
      setAlert({
        show: true,
        message: 'Please select both education status and education level.',
        type: 'warning',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const rf = (field, otherField) =>
        formData[field] === 'Other' && formData[otherField]
          ? formData[otherField]
          : formData[field];

      const updateData = {
        uniqueId: recordData.uniqueId,
        educationStatus: formData.educationStatus,
        educationLevel: rf('educationLevel', 'otherEducationLevel'),
        studyingLocation: formData.studyingLocation,
        schoolType: rf('schoolType', 'otherSchoolType'),
        dropoutReason: rf('dropoutReason', 'otherDropoutReason'),
        literacyLevel: formData.literacyLevel.join(', '),
        usesEducationDevice: formData.usesEducationDevice,
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateEducationDetails(updateData);

      if (response.success) {
        setAlert({
          show: true,
          message: 'Education details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/vocational/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/disability/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={6}
        title="Update Education Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading education details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={6}
        title="Update Education Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load education details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={6}
      title="Update Education Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">Education Status (Editable)</div>

        <Form.Group className="mb-3">
          <Form.Label>
            Education Status <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            name="educationStatus"
            value={formData.educationStatus}
            onChange={handleInputChange}
            required
            disabled={!canEdit}
          >
            <option value="">Select...</option>
            {legendData?.educationStatus?.map((item, index) => (
              <option key={index} value={item}>
                {item}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Education Level <span className="text-danger">*</span>
          </Form.Label>
          <SelectWithOther
            name="educationLevel"
            value={formData.educationLevel}
            otherName="otherEducationLevel"
            otherValue={formData.otherEducationLevel}
            options={legendData?.educationLevel || []}
            onChange={handleInputChange}
            disabled={!canEdit}
            required
            otherPlaceholder="Please specify education level"
          />
        </Form.Group>

        {/* Conditional: Currently Studying Location */}
        {shouldShowStudyingLocation() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Where are you studying?</Form.Label>
            <Form.Select
              name="studyingLocation"
              value={formData.studyingLocation}
              onChange={handleInputChange}
              disabled={!canEdit}
            >
              <option value="">Select...</option>
              <option value="School">School</option>
              <option value="College">College</option>
              <option value="University">University</option>
            </Form.Select>
            <span className="info-badge">For currently studying students</span>
          </Form.Group>
        )}

        {/* Conditional: School Type */}
        {shouldShowSchoolType() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>
              What kind of school are you attending/attended?
            </Form.Label>
            <SelectWithOther
              name="schoolType"
              value={formData.schoolType}
              otherName="otherSchoolType"
              otherValue={formData.otherSchoolType}
              options={legendData?.schoolType || []}
              onChange={handleInputChange}
              disabled={!canEdit}
              otherPlaceholder="Please specify school type"
            />
            <span className="info-badge">For school students or dropouts</span>
          </Form.Group>
        )}

        {/* Conditional: Dropout Reason */}
        {shouldShowDropoutReason() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Reason for school dropout?</Form.Label>
            <SelectWithOther
              name="dropoutReason"
              value={formData.dropoutReason}
              otherName="otherDropoutReason"
              otherValue={formData.otherDropoutReason}
              options={legendData?.dropoutReason || []}
              onChange={handleInputChange}
              disabled={!canEdit}
              otherPlaceholder="Please specify dropout reason"
            />
            <span className="info-badge">For dropout students</span>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Literacy Level (Select all that apply)</Form.Label>
          <div className="literacy-checkbox-group">
            {legendData?.literacyLevel?.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`literacy-${index}`}
                label={item}
                value={item}
                checked={formData.literacyLevel.includes(item)}
                onChange={handleLiteracyChange}
                disabled={!canEdit}
              />
            ))}
          </div>
        </Form.Group>

        {/* Conditional: Assistive Devices for Education */}
        {shouldShowEducationDevices() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>
              Are you using any assistive devices for education?
            </Form.Label>
            <div>
              <Form.Check
                type="radio"
                label="Yes"
                name="usesEducationDevice"
                value="Yes"
                checked={formData.usesEducationDevice === 'Yes'}
                onChange={handleInputChange}
                disabled={!canEdit}
              />
              <Form.Check
                type="radio"
                label="No"
                name="usesEducationDevice"
                value="No"
                checked={formData.usesEducationDevice === 'No'}
                onChange={handleInputChange}
                disabled={!canEdit}
              />
              <Form.Check
                type="radio"
                label="Not using currently, but need an assistive device"
                name="usesEducationDevice"
                value="Not using currently. But need an assistive device"
                checked={
                  formData.usesEducationDevice ===
                  'Not using currently. But need an assistive device'
                }
                onChange={handleInputChange}
                disabled={!canEdit}
              />
            </div>
            <span className="info-badge">For currently studying students</span>
          </Form.Group>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateEducation;
